// Análise de um percurso real face à rota planeada.
//
// Divide o trajecto em troços dentro/fora da rota (desvios), e calcula
// distância percorrida, distância em desvio, nº de desvios e duração.
// Usado pelo mapa do planeamento (polilinhas) e pelo painel de detalhes
// (métricas), a partir da mesma lógica.

import { haversineM } from './trailClean';

// Limiar visual de desvio — distância mínima ao SEGMENTO planeado mais
// próximo para um ponto ser considerado "fora". Apertado (80 m) para
// apanhar desvios reais por ruas paralelas, mas tolerante a ruído de
// GPS típico (±15 m) e a largura de avenidas e nós rodoviários.
// Antes era 250 m e comparava só com VÉRTICES da polyline planeada
// (não com os segmentos), pelo que ruas no meio de troços longos
// apareciam como "off" e desvios curtos ficavam silenciados.
const OFF_M = 80;

// Tamanho mínimo (diagonal da bounding-box, em metros) para considerar
// um troço off-route real. Aglomerados de fixes GPS maus parados no
// mesmo sítio errado têm extensão pequena — descartam-se, evitando que
// um veículo parado em zona de fraca recepção apareça como "em desvio".
const MIN_OFF_SPAN_M = 60;

// Distância (m) do ponto `p` ao SEGMENTO planeado mais próximo.
// Projecção equirectangular local centrada em `p` — erro <0,5 m em
// distâncias <10 km, mais que suficiente para classificar rota.
// Idêntica à usada no LiveRouteMonitor (mantida em sync).
function pointToPolylineMeters(p, planned) {
  if (!Array.isArray(planned) || planned.length === 0) return Infinity;
  // planned é [[lng,lat], ...] (GeoJSON). Para len=1 cai em haversine.
  if (planned.length === 1) return haversineM(p, { lat: planned[0][1], lng: planned[0][0] });
  const cosLat = Math.cos(p.lat * Math.PI / 180);
  const M_PER_DEG = 111320;
  const project = (lng, lat) => ({
    x: (lng - p.lng) * M_PER_DEG * cosLat,
    y: (lat - p.lat) * M_PER_DEG,
  });
  let best = Infinity;
  let prev = project(planned[0][0], planned[0][1]);
  for (let i = 1; i < planned.length; i++) {
    const cur = project(planned[i][0], planned[i][1]);
    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    const len2 = dx * dx + dy * dy;
    let t = 0;
    if (len2 > 0) {
      t = ((-prev.x) * dx + (-prev.y) * dy) / len2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const cx = prev.x + t * dx;
    const cy = prev.y + t * dy;
    const d = Math.sqrt(cx * cx + cy * cy);
    if (d < best) best = d;
    prev = cur;
  }
  return best;
}

// Extensão diagonal da bounding-box de um troço (m). Usada para
// filtrar aglomerados pequenos de fixes maus.
function spanM(path) {
  if (!Array.isArray(path) || path.length === 0) return 0;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const q of path) {
    if (q.lat < minLat) minLat = q.lat; if (q.lat > maxLat) maxLat = q.lat;
    if (q.lng < minLng) minLng = q.lng; if (q.lng > maxLng) maxLng = q.lng;
  }
  return haversineM({ lat: minLat, lng: minLng }, { lat: maxLat, lng: maxLng });
}

/**
 * @param {Array<{lat,lng,timestamp?}>} trail  percurso real (já limpo)
 * @param {Array<[lng,lat]>} plannedCoords     geometria planeada (GeoJSON)
 * @returns {{ segments:Array<{path,off}>, realKm:number, offKm:number,
 *             offCount:number, durMin:number|null }}
 */
export function analyzeTrail(trail, plannedCoords) {
  const pts = Array.isArray(trail) ? trail : [];
  const empty = { segments: [], realKm: 0, offKm: 0, offCount: 0, durMin: null };
  if (pts.length < 2) return empty;

  let realM = 0;
  for (let i = 1; i < pts.length; i++) realM += haversineM(pts[i - 1], pts[i]);

  const t0 = pts[0]?.timestamp ? new Date(pts[0].timestamp).getTime() : null;
  const t1 = pts[pts.length - 1]?.timestamp
    ? new Date(pts[pts.length - 1].timestamp).getTime() : null;
  const durMin = (t0 && t1 && t1 > t0) ? Math.round((t1 - t0) / 60000) : null;

  const planned = Array.isArray(plannedCoords) ? plannedCoords : [];
  if (planned.length < 2) {
    return { segments: [{ path: pts, off: false }], realKm: realM / 1000, offKm: 0, offCount: 0, durMin };
  }

  // Classifica cada fix: distância ao SEGMENTO planeado mais próximo
  // > OFF_M ⇒ fora da rota. Antes comparava só com vértices, o que
  // dava falsos positivos em troços rectos sem vértices intermédios.
  const flags = pts.map((p) => pointToPolylineMeters(p, planned) > OFF_M);

  // Troços contíguos por estado (partilham o ponto de transição).
  const rawSegments = [];
  let cur = { path: [pts[0]], off: flags[0] };
  for (let i = 1; i < pts.length; i++) {
    cur.path.push(pts[i]);
    if (flags[i] !== cur.off) {
      rawSegments.push(cur);
      cur = { path: [pts[i]], off: flags[i] };
    }
  }
  rawSegments.push(cur);

  // Filtra troços off-route minúsculos — aglomerados de fixes maus, não
  // desvios reais. Limiar 60 m alinhado com a tolerância visual OFF_M
  // (80 m). On-route nunca se filtra; é o "estado base" do percurso.
  const segments = rawSegments.filter(s => !(s.off && spanM(s.path) < MIN_OFF_SPAN_M));

  let offM = 0, offCount = 0;
  for (const s of segments) {
    if (!s.off) continue;
    offCount++;
    for (let i = 1; i < s.path.length; i++) offM += haversineM(s.path[i - 1], s.path[i]);
  }

  return { segments, realKm: realM / 1000, offKm: offM / 1000, offCount, durMin };
}
